/* eslint-disable max-lines */
import { NgTemplateOutlet } from '@angular/common';
import {
  QueryList,
  SimpleChanges,
  TemplateRef,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  ViewChild,
  ViewChildren,
  output,
} from '@angular/core';
import type { AbstractControl } from '@angular/forms';
import { NgControl, ReactiveFormsModule, UntypedFormControl } from '@angular/forms';

import { BehaviorSubject, Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

import { emailValidator, filterBoolean, NgTemplateAssertType } from '@core/common-legacy';
import { I18nService } from '@core/i18n-legacy';

import { TaggerDropdownTplContext } from './tagger.types';
import { ButtonComponent } from '../../common/button/components/button/button.component';
import { IconButtonComponent } from '../../common/button/components/icon-button/icon-button.component';
import { DropdownItemComponent } from '../../common/dropdown/dropdown-item/dropdown-item.component';
import { DropdownComponent } from '../../common/dropdown/dropdown.component';
import { IconComponent } from '../../common/icon/icon.component';
import { RtTooltip } from '../../common/tooltip/tooltip.directive';
import { TruncateComponent } from '../../common/truncate/truncate.component';
import { KeyCode } from '../../core/keycode';
import {
  ConnectedOverlayDirective,
  OverlayOriginDirective,
} from '../../core/overlay/overlay-directives';
import type { NotificationRef } from '../../notifications/notification-ref';
import { NotificationsController } from '../../notifications/notifications.controller';
import { ComponentVariant } from '../../utils/variant/variant';
import type { Variants } from '../../utils/variant/variant';
import { InputComponent } from '../input/input.component';
import { TypeaheadInputDirective } from '../typeahead/typeahead-input.directive';
import { TypeaheadSuggestionsComponent } from '../typeahead/typeahead-suggestions.component';
import type { SuggestionsContext } from '../typeahead/typeahead.component';
import { TypeaheadComponent } from '../typeahead/typeahead.component';
import type { Suggestion, TypeaheadSuggestionsState } from '../typeahead/typeahead.service';

/**
 * TODO: This components seems to be broken in many ways
 * It doesn't really support reactive forms and is pain to use with regular model update mechanism
 * Also TaggerTag interface is heavily coupled with app logic,
 */

export type TaggerTag = {
  candidate?: {};
  admin?: {};
  candidateId?: number;
  email: string;
  name?: string;
  type?: string;
  isValid?: boolean;
  error?: string;
  data?: {};
};

export type TaggerSuggestion = {
  id: string;
  value: string;
  kind: string;
  emailObject: TaggerTag;
};

export interface TypeaheadSuggestionsContext<S = Suggestion> {
  state: SuggestionsContext<S>['typeaheadstate'];
  typeahead: TypeaheadComponent;
}

@Component({
  selector: 'rt-tagger',
  templateUrl: './tagger.component.html',
  styleUrls: ['./tagger.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'tagger',
    '[class]': 'variant',
  },
  imports: [
    ButtonComponent,
    ConnectedOverlayDirective,
    DropdownComponent,
    DropdownItemComponent,
    IconButtonComponent,
    IconComponent,
    InputComponent,
    NgTemplateOutlet,
    OverlayOriginDirective,
    ReactiveFormsModule,
    RtTooltip,
    TruncateComponent,
    TypeaheadComponent,
    TypeaheadInputDirective,
    TypeaheadSuggestionsComponent,
    NgTemplateAssertType,
  ],
})
export class TaggerComponent {
  public static readonly VARIANTS: Variants = ['default', ['default', 'email']];
  private static SPACE_KEY_CODE: number = 32;
  private static ENTER_KEY_CODE: number = 13;
  private static COMMA_KEY_CODE: number = 188;

  public readonly SuggestionsContext: SuggestionsContext<any>;
  public readonly DefaultTypeaheadSuggestionsContext: TypeaheadSuggestionsContext;
  public readonly EmailTypeaheadSuggestionsContext: TypeaheadSuggestionsContext<
    Suggestion & { emailObject: { name: string; email: string } }
  >;
  public readonly dropdownConfig: any = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' },
  ];

  @ComponentVariant(TaggerComponent.VARIANTS)
  @Input()
  public variant: string;

  @Input()
  public get tags(): TaggerTag[] {
    return this._tags;
  }

  // TODO: This component uses input mutation in multiple places. It should communicate changes
  // either by updating the passed `control` or by emitting `tagsChange` event.
  public set tags(value: TaggerTag[]) {
    if (value !== this._tags) {
      this._tags = value;
      this.tagsChange.emit(this._tags);
      this.control.setValue(this._tags);
    }
  }

  @Input() public placeholder: string = '';
  @Input()
  public delimiterKeyCodes: number[] = [
    TaggerComponent.ENTER_KEY_CODE,
    TaggerComponent.COMMA_KEY_CODE,
  ];
  @Input() public tagValidator: (control: AbstractControl) => string | null =
    this.defaultTagValidator;
  @Input() public tagTransformation: (
    tag: TaggerTag | TaggerSuggestion | string,
    isValid: boolean,
    error?: string,
  ) => { email: string; isValid: boolean } | TaggerTag = this.defaultTagTransformation;
  @Input() public tagTypeFn: (
    tag: TaggerTag,
  ) => 'admin' | 'candidate' | 'email' | 'default' | void = this.defaultTagTypeFn;
  @Input() public tagTitleFn: (tag: TaggerTag) => string | TaggerTag = this.defaultTagTitleFn;
  @Input() public typeaheadSuggestions: TaggerSuggestion[] = [];
  @Input() public typeaheadSuggestionsTemplate: TemplateRef<TypeaheadSuggestionsContext<any>>;
  @Input() public singleTagTemplate: TemplateRef<any>;
  @Input() public dropdownTemplate?: TemplateRef<TaggerDropdownTplContext>;
  @Input() public control: UntypedFormControl = new UntypedFormControl();
  @Input() public disabled: boolean = false;
  @Input() public readOnly: boolean = false;
  @Input() public enabledEmailChange: boolean = true;
  @Input() public selectCandidate: boolean = false;
  @Input() public markInvalid: boolean = false;
  @Input() public preserveWhiteSpaces: boolean = false;
  @Input() public applyStyleOnFocus: boolean = false;

  public firstEmail = output<string | null>();
  public openCandidate = output<{
    id: number;
  }>();
  public typeaheadChanged = output<string>();
  public tagsChange = output<TaggerTag[]>();
  public deleteLastTag = output<void>();
  public rawValueChanged = output<string | null>();
  public focus = output<void>();
  public blur = output<void>();

  @ViewChild('input', { read: NgControl, static: true }) public input: NgControl;
  @ViewChild('input') public inputCmp: InputComponent;
  @ViewChild('defaultSingleTagTemplate')
  public defaultSingleTagTemplate: TemplateRef<any>;
  @ViewChild('emailSingleTagTemplate', { static: true })
  public emailSingleTagTemplate: TemplateRef<any>;
  @ViewChild('defaultSuggestionsTemplate', { static: true })
  public defaultSuggestionsTemplate: TemplateRef<TypeaheadSuggestionsContext<any>>;
  @ViewChild('emailSuggestionsTemplate', { static: true })
  public emailSuggestionsTemplate: TemplateRef<TypeaheadSuggestionsContext<any>>;
  @ViewChild('taggerTypeahead', { static: true }) public taggerTypeahead: TypeaheadComponent;
  @ViewChildren('tagButton') public tagButtons: QueryList<ButtonComponent>;

  public dummyInputControl: UntypedFormControl = new UntypedFormControl();
  public hasInvalidTags: boolean = false;
  public hasFocusActive: boolean = false;

  private typeaheadSugestionsState: TypeaheadSuggestionsState;
  private errorNotification: NotificationRef | null;
  private _tags: TaggerTag[];
  private error$: BehaviorSubject<string | null> = new BehaviorSubject(null);
  private ngOnDestroy$ = new Subject<void>();

  constructor(
    private i18n: I18nService,
    private notificationsCtrl: NotificationsController,
    private cdRef: ChangeDetectorRef,
  ) {}

  public ngOnInit(): void {
    this.control.setValue(this.tags);
  }

  public ngAfterViewInit(): void {
    this.taggerTypeahead.data$
      .pipe(takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(state => (this.typeaheadSugestionsState = state));

    this.error$
      .pipe(filterBoolean, debounceTime(200), takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(error => {
        this.showError(error!);
      });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if ('disabled' in changes) {
      changes.disabled.currentValue
        ? this.dummyInputControl.disable()
        : this.dummyInputControl.enable();
    }

    if ('readOnly' in changes) {
      changes.readOnly.currentValue
        ? this.dummyInputControl.disable()
        : this.dummyInputControl.enable();
    }
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public toggleDropdown(dropdown: ConnectedOverlayDirective, state?: boolean): void {
    if (state === undefined) {
      dropdown.open = !dropdown.open;
    } else {
      dropdown.open = state;
    }
    this.cdRef.detectChanges();
  }

  public trackByTag<T extends { name?: string }>(index: number, { name }: T): T['name'] {
    return name;
  }

  public trackBySuggestion<T extends { id: any }>(index: number, { id }: T): T['id'] {
    return id;
  }

  public onInput(): void {
    if (!this._tags.length && this.isVariant('email')) {
      const error =
        typeof this.tagValidator === 'function'
          ? this.tagValidator(this.input.control!)
          : this.defaultTagValidator(this.input.control!);

      !error ? this.firstEmail.emit(this.input.value) : this.firstEmail.emit(null);
    }

    this.rawValueChanged.emit(this.input.value);
  }

  public onKeyDown(event: KeyboardEvent): void {
    if (event.keyCode === KeyCode.Backspace && (!this.input.value || !this.input.value.length))
      this.deleteLastTag.emit();

    if (this.errorNotification) {
      this.errorNotification.close();
      this.errorNotification.afterClosed.subscribe(() => (this.errorNotification = null));
    }

    if (
      event.keyCode === TaggerComponent.SPACE_KEY_CODE &&
      this.isVariant('email') &&
      (this.input.value?.indexOf('@') ?? -1) > 0
    ) {
      event.preventDefault();
      this.tryAddTag();
    }

    if (this.delimiterKeyCodes.indexOf(event.keyCode) === -1) {
      return;
    }
    if (
      event.keyCode === TaggerComponent.ENTER_KEY_CODE &&
      this.typeaheadSugestionsState.highlighted
    ) {
      return;
    }
    event.preventDefault();
    this.tryAddTag();
  }

  public onBlur(): void {
    this.tryAddTag();
    this.hasFocusActive = false;
    this.blur.emit();
    this.cdRef.detectChanges();
  }

  public onFocus(): void {
    this.hasFocusActive = true;
    this.focus.emit();
    this.cdRef.detectChanges();
  }

  public deleteTag(tag: TaggerTag, index: number): void {
    if (this.disabled || this.readOnly) return;
    this.tags = this.tags.slice(0, index).concat(this.tags.slice(index + 1));
    this.hasInvalidTags = this.checkInvalidTags();
    this.inputCmp.setFocus();
    this.cdRef.detectChanges();
    this.rawValueChanged.emit(this.input.value);
  }

  public handleClickOnTagger(event: MouseEvent): void {
    const clickedOnSingleTag = this.tagButtons.some(tagButton =>
      tagButton.elRef.nativeElement.contains(event.target),
    );

    if (!clickedOnSingleTag) {
      // @ts-ignore
      this.input.valueAccessor.setFocus();
    }
  }

  public selectedWithTypeahead(suggestion: TaggerSuggestion): void {
    this.newTag(suggestion);
    this.input.control!.setValue('');
  }

  public onEmailSelect({ index, email }: { index: number; email: string }): void {
    this.tags = this.tags.map((tag, i) => (i === index ? { ...tag, email } : tag));
  }

  public hasDropdown(tag: TaggerTag, index: number): boolean {
    return this.enabledEmailChange && (!!this.tags[index].candidate || !!this.tags[index].admin);
  }

  public get defaultTemplate(): TemplateRef<any> {
    return this.isVariant('email') ? this.emailSingleTagTemplate : this.defaultSingleTagTemplate;
  }

  public get suggestionsTemplate(): TemplateRef<TypeaheadSuggestionsContext<any>> {
    if (this.typeaheadSuggestionsTemplate) {
      return this.typeaheadSuggestionsTemplate;
    } else if (this.isVariant('email')) {
      return this.emailSuggestionsTemplate;
    } else {
      return this.defaultSuggestionsTemplate;
    }
  }

  private checkInvalidTags(): boolean {
    if (!this.markInvalid || !Array.isArray(this.tags)) {
      return false;
    }
    return this.tags.filter(tag => !tag.isValid).length > 0;
  }

  private clearInput(): void {
    this.input.control!.setValue('');
    this.typeaheadChanged.emit('');
  }

  private defaultTagValidator(control: AbstractControl): string | null {
    if (this.isVariant('email') && emailValidator(<UntypedFormControl>control)) {
      return this.i18n.translate('rtu.common.invalid_email');
    } else {
      return null;
    }
  }

  private defaultTagTransformation(
    tag: TaggerTag | TaggerSuggestion | string,
    isValid: boolean,
    error?: string,
  ): { email: string; isValid: boolean } | TaggerTag {
    if (this.isVariant('email')) {
      if (typeof tag === 'string') {
        return { email: tag, isValid, ...(error && { error }) };
      } else {
        // @ts-ignore
        return tag.emailObject;
      }
    } else {
      // @ts-ignore
      return tag;
    }
  }

  private defaultTagTypeFn(tag: TaggerTag): 'admin' | 'candidate' | 'email' | 'default' | void {
    if (this.isVariant('email')) {
      if (tag.admin) {
        return 'admin';
      } else if (tag.candidate) {
        return 'candidate';
      } else if (tag.email) {
        return 'email';
      }
    } else {
      return 'default';
    }
  }

  private defaultTagTitleFn(tag: TaggerTag): string | TaggerTag {
    if (this.isVariant('email')) {
      if (tag.name) {
        return `${tag.name} (${tag.email})`;
      } else {
        return tag.email;
      }
    } else {
      return tag;
    }
  }

  private tryAddTag(): void {
    const safeValue = (this.input.value || '').trim();
    const parsedInput = safeValue
      .replace(';', ',')
      .replace(/\s/g, this.preserveWhiteSpaces ? ',' : '');

    parsedInput.split(',').forEach((value: any) => {
      this.input.control!.setValue(value);
      if (value.length === 0) {
        return;
      }
      const error =
        typeof this.tagValidator === 'function'
          ? this.tagValidator(this.input.control!)
          : this.defaultTagValidator(this.input.control!);

      if (this.markInvalid) {
        this.newTag(value, !error, error || undefined);
      } else {
        if (error !== null) {
          return this.error$.next(error);
        }
        this.newTag(value);
      }
    });

    this.clearInput();
  }

  private showError(message: string): void {
    this.errorNotification = this.notificationsCtrl.error(message, {
      showClose: true,
      timeout: 5000,
    });
  }

  private newTag(tag: TaggerTag | TaggerSuggestion, isValid: boolean = true, error?: string): void {
    this.tags = [...this.tags, this.tagTransformation(tag, isValid, error)];
    this.hasInvalidTags = this.checkInvalidTags();
    this.cdRef.detectChanges();
  }

  private isVariant(variant: string): boolean {
    return (this.variant || '').includes(variant) || false;
  }
}
